import { Box } from '@mui/material';
import { Button, Loader, Typography } from '@sm-dashboard/ui-components';
import { ChatAction } from './useCrmAiChat';

/** Card title per write tool. */
const TOOL_TITLES: Record<string, string> = {
  update_contact: 'עדכון פרטי איש קשר',
  create_contact: 'יצירת איש קשר חדש',
  add_custom_field: 'הוספת אפשרות חדשה'
};

/** Hebrew labels for write-tool fields shown in the confirmation card. */
const FIELD_LABELS: Record<string, string> = {
  firstName: 'שם פרטי',
  lastName: 'שם משפחה',
  nickname: 'כינוי',
  phone: 'טלפון',
  phone2: 'טלפון נוסף',
  mail: 'מייל',
  addressName: 'כתובת',
  notes: 'הערות',
  gender: 'מגדר',
  maritalStatus: 'מצב משפחתי',
  jewishBirthDate: 'תאריך לידה עברי',
  jewishWeddingDate: 'תאריך נישואין עברי',
  jewishFatherYahrzeit: 'יארצייט האב',
  jewishMotherYahrzeit: 'יארצייט האם',
  jewishDeathDate: 'תאריך פטירה עברי',
  fatherFullName: 'שם האב',
  motherFullName: 'שם האם',
  employmentStatus: 'תעסוקה',
  interestsAndHobbies: 'תחומי עניין ותחביבים',
  sensitivitiesAndLimitations: 'רגישויות ומגבלות',
  skillsAndAbilities: 'כישורים ויכולות',
  importantToRemember: 'חשוב לזכור',
  religiousBackground: 'רקע דתי',
  initialContact: 'היכרות ראשונית',
  tags: 'תגיות',
  circleOption: 'מעגלים',
  institutionConnectionsOption: 'קשר למוסד',
  interestInActivitiesOption: 'עניין בפעילויות',
  title: 'תואר',
  type: 'סוג',
  value: 'ערך'
};

// Args that are routing/identity data, not user-facing field changes.
const HIDDEN_ARGS = ['phoneCountry', 'phone2Country', 'contactId'];

const GENDER_LABELS: Record<string, string> = { male: 'זכר', female: 'נקבה' };
const MARITAL_LABELS: Record<string, string> = {
  single: 'רווק/ה',
  married: 'נשוי/אה',
  divorced: 'גרוש/ה',
  widowed: 'אלמן/ה',
  other: 'אחר'
};
const CUSTOM_FIELD_TYPE_LABELS: Record<string, string> = {
  circles: 'מעגלים',
  institutionConnections: 'קשר למוסד',
  interestInActivities: 'עניין בפעילויות'
};

const formatValue = (field: string, value: unknown): string => {
  if (field === 'gender') return GENDER_LABELS[String(value)] || String(value);
  if (field === 'maritalStatus') return MARITAL_LABELS[String(value)] || String(value);
  if (field === 'type') return CUSTOM_FIELD_TYPE_LABELS[String(value)] || String(value);
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

const STATUS_TEXT: Record<string, string> = {
  done: 'העדכון אושר ובוצע ✓',
  failed: 'העדכון נכשל',
  rejected: 'העדכון בוטל'
};

interface ChatActionCardProps {
  action: ChatAction;
  disabled: boolean;
  onApprove: () => void;
  onReject: () => void;
}

/** Confirmation card for a write action the assistant proposed — nothing runs until approved here. */
const ChatActionCard = ({ action, disabled, onApprove, onReject }: ChatActionCardProps) => {
  const changes = Object.entries(action.args).filter(([key]) => !HIDDEN_ARGS.includes(key));

  return (
    <Box
      sx={{
        alignSelf: 'flex-end',
        maxWidth: '85%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '12px',
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1
      }}
    >
      <Typography variant="h6" color="text.primary">
        {TOOL_TITLES[action.tool] || 'אישור פעולה'}
      </Typography>

      <Box component="ul" sx={{ m: 0, pl: 0, pr: 2, fontSize: 14, lineHeight: 1.6 }}>
        {action.display?.contactName && (
          <li>
            <Box component="span" sx={{ color: 'text.secondary' }}>
              איש קשר:{' '}
            </Box>
            {action.display.contactName}
          </li>
        )}
        {changes.map(([field, value]) => (
          <li key={field}>
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {FIELD_LABELS[field] || field}:{' '}
            </Box>
            {formatValue(field, value)}
          </li>
        ))}
      </Box>

      {action.status === 'pending' && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" size="small" fullWidth={false} disabled={disabled} onClick={onApprove}>
            אישור
          </Button>
          <Button variant="outlined" size="small" fullWidth={false} disabled={disabled} onClick={onReject}>
            ביטול
          </Button>
        </Box>
      )}

      {action.status === 'executing' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontSize: 13 }}>
          <Loader size={14} />
          מעדכן...
        </Box>
      )}

      {STATUS_TEXT[action.status] && (
        <Box
          sx={{
            fontSize: 13,
            color: action.status === 'done' ? 'success.main' : action.status === 'failed' ? 'error.main' : 'text.secondary'
          }}
        >
          {STATUS_TEXT[action.status]}
        </Box>
      )}
    </Box>
  );
};

export default ChatActionCard;
